import axios from 'axios';

/** 'exec' is the mixed-format instrument's respondent id; 'executive' is retained for the
 *  legacy narrative keys that predate it and still resolve server-side. */
export type AdvancedRole = 'exec' | 'executive' | 'it' | 'process_owner' | 'joint';
export type EvidenceReviewStatus = 'needs_review' | 'supported' | 'rejected';

export interface AdvancedSession {
  id: string;
  organizationName: string;
  executiveName: string;
  executiveEmail: string;
  status: string;
  accessStatus: 'pending_payment' | 'paid' | 'waived' | 'refunded' | 'support_required';
  methodologyVersion: string;
}

export interface AdvancedResponse {
  id: string;
  role: AdvancedRole;
  questionKey: string;
  response: string;
  wordCount: number;
}

export interface AdvancedEvidence {
  id: string;
  responseId: string;
  role: AdvancedRole;
  questionKey: string;
  construct: string;
  claim: string;
  sourceQuote: string;
  confidence: 'high' | 'medium' | 'low';
  status: EvidenceReviewStatus;
  correctedClaim?: string | null;
  riskFlags: string[];
  followUp?: string;
}

export interface AdvancedSessionEnvelope {
  session: AdvancedSession;
  responses: AdvancedResponse[];
  evidence: AdvancedEvidence[];
}

export type AdvancedCommerceState =
  | 'checkout'
  | 'reservation_unavailable'
  | 'processing'
  | 'cancelled'
  | 'expired'
  | 'failed'
  | 'paid'
  | 'refunded'
  | 'support_required';

/** A server-authoritative commerce view. It deliberately contains no payment or card data. */
export interface AdvancedCommerceStatus {
  state: AdvancedCommerceState;
  supportReference?: string;
  checkoutUrl?: string;
  offer?: {
    priceCad: 99 | 500;
    foundingAvailable: boolean;
    reservationAvailable: boolean;
    available: boolean;
  };
}

export interface AdvancedOfferStatus {
  tier: 'founding' | 'standard';
  priceCad: 99 | 500;
  foundingAvailable: boolean;
  reservationAvailable: boolean;
  available: boolean;
  availabilityReason?: 'checkout_paused' | 'founding_reservations_in_progress';
}

interface CreateSessionResponse extends AdvancedSessionEnvelope {
  success: true;
  accessToken: string;
  paymentRequired: boolean;
}

const errorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.error;
    if (typeof message === 'string' && message.trim()) {
      const issues = error.response?.data?.issues;
      return Array.isArray(issues) && issues.length
        ? `${message} ${issues.slice(0, 3).map((issue: { prompt: string; message: string }) => `${issue.prompt} ${issue.message}`).join(' ')}`
        : message;
    }
  }
  return fallback;
};

const authHeaders = (accessToken: string) => ({
  headers: { Authorization: `Bearer ${accessToken}` }
});

export const validateRedemptionCode = async (code: string) => {
  try {
    const response = await axios.post('/api/assessment/advanced/redemptions/validate', { code });
    return response.data as { valid: true; accessStatus: 'waived'; message: string };
  } catch (error) {
    throw new Error(errorMessage(error, 'That redemption code could not be validated.'));
  }
};

export const getAdvancedOfferStatus = async () => {
  try {
    const response = await axios.get('/api/assessment/advanced/offer-status');
    return response.data as AdvancedOfferStatus;
  } catch (error) {
    throw new Error(errorMessage(error, 'Secure checkout availability is temporarily unavailable.'));
  }
};

export const createAdvancedSession = async (input: {
  organizationName: string;
  executiveName: string;
  executiveEmail: string;
  redemptionCode?: string;
  consent: boolean;
}) => {
  try {
    const response = await axios.post('/api/assessment/advanced/sessions', input);
    return response.data as CreateSessionResponse;
  } catch (error) {
    throw new Error(errorMessage(error, 'The assessment could not be started.'));
  }
};

export const createAdvancedCheckout = async (sessionId: string, accessToken: string) => {
  try {
    const response = await axios.post(
      `/api/assessment/advanced/sessions/${sessionId}/checkout`,
      {},
      authHeaders(accessToken)
    );
    return response.data as AdvancedCommerceStatus & { paymentRequired: boolean };
  } catch (error) {
    throw new Error(errorMessage(error, 'Secure checkout is temporarily unavailable.'));
  }
};

/**
 * Planned commerce endpoint. The browser can only display the server's order state;
 * it never infers payment success from the Checkout redirect.
 */
export const getAdvancedCommerceStatus = async (sessionId: string, accessToken: string) => {
  try {
    const response = await axios.get(
      `/api/assessment/advanced/sessions/${sessionId}/commerce-status`,
      authHeaders(accessToken)
    );
    return response.data as AdvancedCommerceStatus;
  } catch (error) {
    throw new Error(errorMessage(error, 'Payment status is temporarily unavailable.'));
  }
};

/** Planned, rate-limited recovery endpoint; callers provide only the address required for delivery. */
export const requestAdvancedRecovery = async (input: { sessionId: string; email: string }) => {
  try {
    const response = await axios.post('/api/assessment/advanced/recovery', input);
    return response.data as { accepted: true; resendAvailableInSeconds: number };
  } catch (error) {
    throw new Error(errorMessage(error, 'We could not send a recovery link. Please try again later.'));
  }
};

/** Deliberate POST exchange. Merely opening the email link never restores access. */
export const exchangeAdvancedRecovery = async (token: string) => {
  try {
    const response = await axios.post('/api/assessment/advanced/recovery/exchange', { token });
    return response.data as { success: true; sessionId: string; accessToken: string };
  } catch (error) {
    throw new Error(errorMessage(error, 'This recovery link is invalid or expired. Request a new private link.'));
  }
};

export const getAdvancedSession = async (sessionId: string, accessToken: string) => {
  try {
    const response = await axios.get(
      `/api/assessment/advanced/sessions/${sessionId}`,
      authHeaders(accessToken)
    );
    return response.data as AdvancedSessionEnvelope;
  } catch (error) {
    throw new Error(errorMessage(error, 'This private assessment session could not be resumed.'));
  }
};

export const saveAdvancedResponse = async (
  sessionId: string,
  accessToken: string,
  input: { role: AdvancedRole; questionKey: string; response: string }
) => {
  try {
    const response = await axios.put(
      `/api/assessment/advanced/sessions/${sessionId}/responses`,
      input,
      authHeaders(accessToken)
    );
    return response.data.response as AdvancedResponse;
  } catch (error) {
    throw new Error(errorMessage(error, 'The response could not be saved.'));
  }
};

export type InstrumentFormat =
  | 'single_choice'
  | 'forced_choice'
  | 'true_false'
  | 'short_answer'
  | 'long_answer';

export interface InstrumentOption {
  value: string;
  label: string;
  description?: string;
  unknown?: boolean;
}

export interface InstrumentItem {
  id: string;
  format: InstrumentFormat;
  prompt: string;
  guidance?: string;
  required: boolean;
  options?: InstrumentOption[];
  shownWhen?: { item: string; value?: string; values?: string[] };
  /** Draw options from another item's affirmative rows, so a respondent is never offered a
   *  choice they have just ruled out. */
  optionsFrom?: string;
  minWords?: number;
  maxWords?: number;
  maxLength?: number;
}

export interface InstrumentSection {
  id: string;
  label: string;
  items: InstrumentItem[];
}

export type AssessmentAnswers = Record<string, string | Record<string, boolean | null> | undefined>;

export interface InstrumentPayload {
  answers?: AssessmentAnswers;
  methodologyVersion?: string;
  submitted?: boolean;
  shortlist?: { area: string; label: string; why: string[] }[];
  respondent: 'exec' | 'it';
  instrumentDigest: string;
  sections: InstrumentSection[];
  itemCount: number;
}

export const getAdvancedInstrument = async (
  sessionId: string,
  accessToken: string,
  respondent: 'exec' | 'it' = 'exec'
) => {
  try {
    const response = await axios.get(
      `/api/assessment/advanced/sessions/${sessionId}/instrument?respondent=${respondent}`,
      authHeaders(accessToken)
    );
    return response.data as InstrumentPayload;
  } catch (error) {
    throw new Error(errorMessage(error, 'The questionnaire could not be loaded.'));
  }
};

/** Structured answers post `answer`; narratives post `response`. */
export const saveAdvancedAnswer = async (
  sessionId: string,
  accessToken: string,
  input: {
    role: AdvancedRole;
    questionKey: string;
    answer?: string | Record<string, boolean | null>;
    response?: string;
    draft?: boolean;
  }
) => {
  try {
    const response = await axios.put(
      `/api/assessment/advanced/sessions/${sessionId}/responses`,
      input,
      authHeaders(accessToken)
    );
    return response.data.response as AdvancedResponse;
  } catch (error) {
    throw new Error(errorMessage(error, 'That answer could not be saved.'));
  }
};

export const extractAdvancedEvidence = async (sessionId: string, accessToken: string) => {
  try {
    const response = await axios.post(
      `/api/assessment/advanced/sessions/${sessionId}/evidence/extract`,
      {},
      authHeaders(accessToken)
    );
    return response.data as AdvancedSessionEnvelope;
  } catch (error) {
    throw new Error(errorMessage(error, 'Evidence processing is temporarily unavailable.'));
  }
};

export const reviewAdvancedEvidence = async (
  sessionId: string,
  accessToken: string,
  evidenceId: string,
  status: EvidenceReviewStatus
) => {
  try {
    const response = await axios.patch(
      `/api/assessment/advanced/sessions/${sessionId}/evidence/${evidenceId}`,
      { status },
      authHeaders(accessToken)
    );
    return response.data.evidence as AdvancedEvidence;
  } catch (error) {
    throw new Error(errorMessage(error, 'The evidence decision could not be saved.'));
  }
};


export interface AdvancedReportStatus {
  state: 'not_requested' | 'pending' | 'generating' | 'ready' | 'failed' | 'superseded';
  formats?: string[];
  version?: number;
  deliveredAt?: string | null;
}

export const submitAdvancedAssessment = async (sessionId: string, accessToken: string) => {
  try {
    await axios.post(`/api/assessment/advanced/sessions/${sessionId}/score`, {}, authHeaders(accessToken));
  } catch (error) {
    throw new Error(errorMessage(error, 'Your assessment could not be submitted. Your saved answers are safe.'));
  }
};

export const getAdvancedReportStatus = async (sessionId: string, accessToken: string) => {
  try {
    const response = await axios.get(`/api/assessment/advanced/sessions/${sessionId}/report`, authHeaders(accessToken));
    return response.data as AdvancedReportStatus;
  } catch (error) {
    throw new Error(errorMessage(error, 'Report status is temporarily unavailable. Please try again.'));
  }
};

export const downloadAdvancedReport = async (sessionId: string, accessToken: string) => {
  try {
    const response = await axios.get(`/api/assessment/advanced/sessions/${sessionId}/report/pdf`, {
      ...authHeaders(accessToken), responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Davinci-business-assessment.pdf';
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (error) {
    throw new Error(errorMessage(error, 'The report could not be downloaded. Please retry or contact support.'));
  }
};
